import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';

import type { AdminCreditPricingConfigRow, AdminCreditSowRow } from '../../../services/api';
import { cn, pluralize } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { ConfirmDialog } from '../../ui/confirm-dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    getClickableRowProps,
} from '../../ui/table';
import { DialogFrame } from '../credit/creditDialogPrimitives';
import { AdminActiveStatusBadge } from '../credit/AdminActiveStatusBadge';
import { formatEnumLabel } from '../credit/adminCreditAdapters';
import { ADMIN_BADGE_BASE_CLASS } from '../credit/adminStatusBadge';
import { AdminDashboardEmptyState } from '../AdminDashboardPrimitives';
import type { ActivePricingUsageTarget, PricingConfigsView } from '../adminDashboardTypes';

function formatAdminDate(value: string | undefined): string {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    }).format(date);
}

function GroupedPricingBadge() {
    return (
        <span className={cn(ADMIN_BADGE_BASE_CLASS, 'shrink-0 bg-neutral-200 text-neutral-800')}>
            Grouped
        </span>
    );
}

function pricingConfigFormerNames(
    rows: AdminCreditPricingConfigRow[],
    latestName: string,
): string | null {
    const latestNormalized = latestName.trim();
    const distinct = [...new Set(rows.map((row) => row.name.trim()))].filter(
        (name) => name !== latestNormalized,
    );
    if (distinct.length === 0) {
        return null;
    }
    return distinct.sort().join(', ');
}

function groupPricingConfigs(rows: AdminCreditPricingConfigRow[]) {
    const byGroup = new Map<string, AdminCreditPricingConfigRow[]>();
    for (const row of rows) {
        const groupId = row.pricingConfigGroupId || row.id;
        byGroup.set(groupId, [...(byGroup.get(groupId) ?? []), row]);
    }

    return Array.from(byGroup.entries()).map(([groupId, groupRows]) => {
        const sortedRows = [...groupRows].sort((a, b) => {
            if (b.version !== a.version) return b.version - a.version;
            return Date.parse(b.createdAt ?? '') - Date.parse(a.createdAt ?? '');
        });
        return {
            groupId,
            rows: sortedRows,
            latest: sortedRows[0],
        };
    });
}

function PricingConfigActiveUsageButton({
    count,
    onClick,
    buttonText,
    buttonLabel,
    helperText,
}: {
    count: number;
    onClick: (event: { stopPropagation?: () => void }) => void;
    buttonText?: string;
    buttonLabel: string;
    helperText?: string;
}) {
    if (count <= 0) return <span>-</span>;

    return (
        <div className="space-y-1">
            <button
                type="button"
                className={cn(
                    'inline-flex min-h-8 min-w-14 shrink-0 cursor-pointer items-center justify-center rounded-full',
                    'border border-primary-500/25 bg-primary-500/10 px-3 py-1.5',
                    'text-sm font-semibold tabular-nums text-primary-500',
                    'transition-colors hover:border-primary-500/40 hover:bg-primary-500/18 active:bg-primary-500/22',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                )}
                aria-label={buttonLabel}
                onClick={(event) => {
                    event.stopPropagation();
                    onClick(event);
                }}
            >
                {buttonText ?? String(count)}
            </button>
            {helperText ? <p className="text-xs text-neutral-500">{helperText}</p> : null}
        </div>
    );
}

function PricingConfigArchiveButton({
    config,
    onArchive,
}: {
    config: AdminCreditPricingConfigRow;
    onArchive: (pricingConfigId: string, event?: { stopPropagation?: () => void }) => void;
}) {
    const disabled = !config.isActive || config.activeSowCount !== 0;
    const label = !config.isActive
        ? 'Archived'
        : config.activeSowCount !== 0
          ? 'In use'
          : 'Archive';

    return (
        <Button
            type="button"
            variant={disabled ? 'outline' : 'destructiveOutline'}
            size="sm"
            disabled={disabled}
            onClick={(event) => {
                event.stopPropagation();
                onArchive(config.id, event);
            }}
        >
            {label}
        </Button>
    );
}

function PricingConfigFlatTableRow({
    config,
    onRowClick,
    onOpenActiveUsage,
    onArchivePricingConfig,
    nested,
    isFirstInNestedBlock,
    isLastInNestedBlock,
}: {
    config: AdminCreditPricingConfigRow;
    onRowClick: (row: AdminCreditPricingConfigRow) => void;
    onOpenActiveUsage: (
        target: ActivePricingUsageTarget,
        description: string,
        event?: { stopPropagation?: () => void },
    ) => void;
    onArchivePricingConfig: (
        pricingConfigId: string,
        event?: { stopPropagation?: () => void },
    ) => void;
    nested?: boolean;
    isFirstInNestedBlock?: boolean;
    isLastInNestedBlock?: boolean;
}) {
    const isNested = Boolean(nested);
    return (
        <TableRow
            className={cn(
                'cursor-pointer hover:bg-neutral-50',
                isNested && 'bg-primary-500/[0.07] hover:bg-primary-500/[0.1]',
                isNested && isFirstInNestedBlock && 'border-t-2 border-t-neutral-300',
                isNested && isLastInNestedBlock && 'border-b-2 border-b-neutral-300',
            )}
            {...getClickableRowProps(() => onRowClick(config))}
        >
            <TableCell
                className={cn(isNested && 'border-l-4 border-l-primary-500', isNested && 'pl-5')}
            >
                <p className="font-medium text-neutral-950">{config.name}</p>
                <p className="text-xs text-neutral-500">{config.id}</p>
            </TableCell>
            <TableCell>v{config.version}</TableCell>
            <TableCell className="whitespace-nowrap">{formatAdminDate(config.createdAt)}</TableCell>
            <TableCell className="whitespace-nowrap">
                <AdminActiveStatusBadge isActive={config.isActive} />
            </TableCell>
            <TableCell>
                <PricingConfigActiveUsageButton
                    count={config.activeSowCount}
                    buttonLabel={`${config.activeSowCount} active ${pluralize(
                        config.activeSowCount,
                        'SOW',
                        'SOWs',
                    )}`}
                    onClick={(event) =>
                        onOpenActiveUsage(
                            { pricingConfigIds: [config.id] },
                            `${config.name} v${config.version} active SOW usage`,
                            event,
                        )
                    }
                />
            </TableCell>
            <TableCell>{config.description || '-'}</TableCell>
            <TableCell>
                <PricingConfigArchiveButton config={config} onArchive={onArchivePricingConfig} />
            </TableCell>
        </TableRow>
    );
}

function PricingConfigTableHeader() {
    return (
        <TableHeader>
            <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Active usage</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Archive</TableHead>
            </TableRow>
        </TableHeader>
    );
}

function GroupedPricingConfigs({
    rows,
    onRowClick,
    onOpenActiveUsage,
    onArchivePricingConfig,
}: {
    rows: AdminCreditPricingConfigRow[];
    onRowClick: (row: AdminCreditPricingConfigRow) => void;
    onOpenActiveUsage: (
        target: ActivePricingUsageTarget,
        description: string,
        event?: { stopPropagation?: () => void },
    ) => void;
    onArchivePricingConfig: (
        pricingConfigId: string,
        event?: { stopPropagation?: () => void },
    ) => void;
}) {
    const groups = groupPricingConfigs(rows);
    const [expandedGroupIds, setExpandedGroupIds] = useState<Record<string, boolean>>({});

    return (
        <Card className="overflow-clip">
            <Table stickyHeader aria-label="Pricing configurations" className="min-w-[48rem]">
                <PricingConfigTableHeader />
                <TableBody>
                    {groups.map((group) => {
                        if (group.rows.length === 1) {
                            const only = group.rows[0]!;
                            return (
                                <PricingConfigFlatTableRow
                                    key={only.id}
                                    config={only}
                                    onRowClick={onRowClick}
                                    onOpenActiveUsage={onOpenActiveUsage}
                                    onArchivePricingConfig={onArchivePricingConfig}
                                />
                            );
                        }

                        const latest = group.latest;
                        const expanded = !!expandedGroupIds[group.groupId];
                        const formerNames = pricingConfigFormerNames(group.rows, latest.name);
                        const activeSowCountTotal = latest.activeSowGroupCount;

                        return (
                            <Fragment key={group.groupId}>
                                <TableRow className="bg-neutral-50/90 hover:bg-neutral-50">
                                    <TableCell className="align-top text-neutral-800">
                                        <div className="flex items-start justify-between gap-2">
                                            <GroupedPricingBadge />
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setExpandedGroupIds((prev) => ({
                                                        ...prev,
                                                        [group.groupId]: !prev[group.groupId],
                                                    }))
                                                }
                                                className="-m-1 mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded text-neutral-600 hover:bg-neutral-200/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                                aria-expanded={expanded}
                                                aria-describedby={`pricing-group-${group.groupId}-name`}
                                                aria-label={
                                                    expanded
                                                        ? 'Collapse versions'
                                                        : 'Expand versions'
                                                }
                                            >
                                                {expanded ? (
                                                    <ChevronDown className="size-4" aria-hidden />
                                                ) : (
                                                    <ChevronRight className="size-4" aria-hidden />
                                                )}
                                            </button>
                                        </div>
                                        <p
                                            id={`pricing-group-${group.groupId}-name`}
                                            className="mt-2 font-medium text-neutral-950"
                                        >
                                            {latest.name}
                                        </p>
                                        <p className="text-xs text-neutral-500">
                                            Group {group.groupId} · {group.rows.length} versions
                                        </p>
                                        {formerNames ? (
                                            <p className="mt-1 text-xs text-neutral-500">
                                                Earlier names: {formerNames}
                                            </p>
                                        ) : null}
                                    </TableCell>
                                    <TableCell className="align-top font-medium tabular-nums">
                                        v{latest.version}
                                    </TableCell>
                                    <TableCell className="align-top whitespace-nowrap text-sm">
                                        {formatAdminDate(latest.createdAt)}
                                    </TableCell>
                                    <TableCell className="whitespace-nowrap align-top">
                                        <AdminActiveStatusBadge isActive={latest.isActive} />
                                    </TableCell>
                                    <TableCell className="align-top">
                                        <PricingConfigActiveUsageButton
                                            count={activeSowCountTotal}
                                            buttonText={`${activeSowCountTotal} total`}
                                            buttonLabel={`${activeSowCountTotal} total active ${pluralize(
                                                activeSowCountTotal,
                                                'SOW',
                                                'SOWs',
                                            )}`}
                                            helperText="sum of versions"
                                            onClick={(event) =>
                                                onOpenActiveUsage(
                                                    { pricingConfigGroupId: group.groupId },
                                                    `${latest.name} active SOW usage across versions`,
                                                    event,
                                                )
                                            }
                                        />
                                    </TableCell>
                                    <TableCell className="align-top">
                                        {latest.description?.trim() ? latest.description : '—'}
                                    </TableCell>
                                    <TableCell className="align-top">-</TableCell>
                                </TableRow>
                                {expanded &&
                                    group.rows.map((config, index, arr) => (
                                        <PricingConfigFlatTableRow
                                            key={config.id}
                                            config={config}
                                            onRowClick={onRowClick}
                                            onOpenActiveUsage={onOpenActiveUsage}
                                            onArchivePricingConfig={onArchivePricingConfig}
                                            nested
                                            isFirstInNestedBlock={index === 0}
                                            isLastInNestedBlock={index === arr.length - 1}
                                        />
                                    ))}
                            </Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </Card>
    );
}

export function AdminPricingTable({
    rows,
    view,
    onRowClick,
    onOpenActiveUsage,
    onArchivePricingConfig,
}: {
    rows: AdminCreditPricingConfigRow[];
    view: PricingConfigsView;
    onRowClick: (row: AdminCreditPricingConfigRow) => void;
    onOpenActiveUsage: (
        target: ActivePricingUsageTarget,
        description: string,
        event?: { stopPropagation?: () => void },
    ) => void;
    onArchivePricingConfig: (
        pricingConfigId: string,
        event?: { stopPropagation?: () => void },
    ) => void;
}) {
    if (rows.length === 0) {
        return <AdminDashboardEmptyState message="No pricing configurations found." />;
    }

    if (view === 'grouped') {
        return (
            <GroupedPricingConfigs
                rows={rows}
                onRowClick={onRowClick}
                onOpenActiveUsage={onOpenActiveUsage}
                onArchivePricingConfig={onArchivePricingConfig}
            />
        );
    }

    return (
        <Card className="overflow-clip">
            <Table stickyHeader aria-label="Pricing configurations" className="min-w-[48rem]">
                <PricingConfigTableHeader />
                <TableBody>
                    {rows.map((config) => (
                        <PricingConfigFlatTableRow
                            key={config.id}
                            config={config}
                            onRowClick={onRowClick}
                            onOpenActiveUsage={onOpenActiveUsage}
                            onArchivePricingConfig={onArchivePricingConfig}
                        />
                    ))}
                </TableBody>
            </Table>
        </Card>
    );
}

export function PricingConfigActiveUsageDialog({
    open,
    description,
    isLoading,
    sows,
    onClose,
}: {
    open: boolean;
    description?: string;
    isLoading: boolean;
    sows: AdminCreditSowRow[];
    onClose: () => void;
}) {
    return (
        <DialogFrame
            open={open}
            onClose={onClose}
            title="Active SOWs"
            description={description}
            panelClassName="max-w-3xl"
        >
            {isLoading ? (
                <p className="text-sm text-neutral-500">Loading active SOWs…</p>
            ) : sows.length === 0 ? (
                <p className="text-sm text-neutral-500">No active SOWs use this pricing version.</p>
            ) : (
                <div className="space-y-3">
                    {sows.map((sow) => (
                        <div
                            key={sow.id}
                            className="rounded-lg border border-neutral-200 bg-white p-4"
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <Link
                                        to={`/admin/sows/${sow.id}`}
                                        className="font-medium text-primary-500 hover:text-primary-600 hover:underline"
                                    >
                                        {sow.name}
                                    </Link>
                                    <p className="mt-1 text-sm text-neutral-500">
                                        {sow.companyName} · {formatEnumLabel(sow.state)}
                                    </p>
                                </div>
                                <p className="text-sm text-neutral-500">
                                    {formatAdminDate(sow.endDate ?? undefined)}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </DialogFrame>
    );
}

export function PricingConfigArchiveConfirmDialog({
    open,
    versionSubtitle,
    isPending,
    onCancel,
    onConfirm,
}: {
    open: boolean;
    versionSubtitle?: string;
    isPending: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}) {
    return (
        <ConfirmDialog
            open={open}
            title="Archive pricing version?"
            description={
                <>
                    {versionSubtitle ? (
                        <span className="block font-medium text-neutral-900">
                            {versionSubtitle}
                        </span>
                    ) : null}
                    This version will be hidden from default lists and cannot be selected for new
                    SOWs.
                </>
            }
            confirmLabel="Archive"
            pendingLabel="Archiving…"
            destructive
            isPending={isPending}
            onConfirm={onConfirm}
            onCancel={onCancel}
        />
    );
}
